import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import useCanonical, { SITE, DEFAULT_TITLE } from "../lib/useCanonical";
import usePageMeta, { DEFAULT_DESCRIPTION } from "../lib/usePageMeta";
import ArticleCard from "../components/ArticleCard";
import CategoryTiles from "../components/CategoryTiles";
import { GlossaryBand } from "../components/HomeBands";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/f872ff36-64d6-47d7-a5e3-30de7620380a/images/49e6909971265de6d6ba081bc7ef728fe89c1c89a1269a8f0715da1bc0fb822b.jpeg";

const HOME_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${SITE}/#webpage`,
  url: `${SITE}/`,
  name: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  isPartOf: { "@id": "https://thetealist.com/#website" },
  about: { "@id": "https://thetealist.com/#org" },
  inLanguage: "en-AU",
};

export default function Home() {
  useCanonical("/");
  usePageMeta({ title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, jsonLd: HOME_LD });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http
      .get("/posts?limit=50")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
    // `http` is a stable module import; setters are stable React refs.
  }, []);  

  const featured = posts.find((p) => p.featured) || posts[0];
  const secondary = posts.filter((p) => p.slug !== featured?.slug).slice(0, 2);
  const rest = posts.filter((p) => p.slug !== featured?.slug && !secondary.find((s) => s.slug === p.slug));

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32 text-center">
        <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
          steeping…
        </p>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="relative z-10">
      {/* HERO — split: cream/sage text panel + bright image */}
      <section
        className="relative w-full overflow-hidden border-b"
        style={{ borderColor: "var(--border)", background: "linear-gradient(160deg, var(--bg-sage-from) 0%, var(--bg-sage-to) 100%)" }}
        data-testid="hero-banner"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 min-h-0 lg:min-h-[600px] lg:max-h-[720px]">
          <div className="order-1 lg:order-2 lg:col-span-6 xl:col-span-7 relative">
            <div className="aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:absolute lg:inset-0 lg:rounded-l-lg overflow-hidden">
              <img
                src={HERO_IMAGE}
                alt="Tea estate at golden hour — deep green tea bushes winding towards a heritage tea factory beneath misty mountains"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 55%" }}
                data-testid="hero-image"
              />
            </div>
          </div>

          <div
            className="order-2 lg:order-1 lg:col-span-6 xl:col-span-5 min-w-0 flex items-center px-6 md:px-12 lg:pr-12 py-12 md:py-16 lg:py-20"
          >
            <div className="w-full min-w-0 max-w-2xl hero-reveal">
              <p className="overline mb-5">A comprehensive Tea Journal</p>
              <h1
                className="font-serif font-light tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-5xl 2xl:text-[3.4rem]"
                style={{ color: "#10301f" }}
              >
                Discover your next cup,
                <br />
                <span className="italic" style={{ color: "var(--burgundy)" }}>one tea at a time…</span>
              </h1>
              <p className="mt-6 md:mt-8 text-sm md:text-base font-light leading-relaxed text-[#404e48]">
                Welcome to The Tea List, the definitive destination for tea lovers,
                connoisseurs, &amp; the curious. Whether you&apos;re chasing your next
                single-origin tea, curious about the science of terroir &amp; processing,
                or learning to steep the perfect pot — you belong here..!
              </p>
              <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
                <Link to="/blog" className="btn-primary" data-testid="hero-cta-journal">
                  Read the Journal
                </Link>
                <Link to="/category/brewing-and-the-art-of-tasting" className="btn-secondary" data-testid="hero-cta-brewing">
                  Brewing guides →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-y" style={{ backgroundColor: "var(--bg-alt)", borderColor: "var(--border)" }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="mb-8 md:mb-10">
          <p className="overline">Editor&apos;s pick</p>
        </div>
        {featured && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
            <Link
              to={`/blog/${featured.slug}`}
              className="lg:col-span-8 card-img block group"
              data-testid="featured-post-link"
            >
              <div className="aspect-[16/10] overflow-hidden rounded-card bg-[#f1eadb]">
                <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
              </div>
              <div className="pt-6 max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1e4a35]">
                    {featured.category}
                  </span>
                  <span className="h-1 w-1 rounded-full bg-[#66726d]" />
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#66726d]">
                    {featured.read_time} min
                  </span>
                </div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] group-hover:text-[#1e4a35] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-5 text-lg font-light text-[#404e48] leading-relaxed">
                  {featured.excerpt}
                </p>
                <p className="mt-6 font-mono text-[10px] tracking-widest uppercase text-[#66726d]">
                  by {featured.author_name}
                </p>
              </div>
            </Link>

            <div className="lg:col-span-4 flex flex-col gap-10">
              {secondary.map((p) => (
                <ArticleCard key={p.slug} post={p} size="sm" />
              ))}
            </div>
          </div>
        )}
      </div>
      </section>

      <CategoryTiles posts={posts} />

      {/* LATEST GRID */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline mb-3">From the journal</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
              Recent dispatches
            </h2>
          </div>
          <Link
            to="/blog"
            className="hidden md:inline-flex items-center gap-2 border-b-[1.5px] border-[#e2d9c6] pb-0.5 text-sm font-medium text-[#151e1a] hover:text-[#1e4a35] hover:border-[#1e4a35] transition-colors"
            data-testid="see-all-posts-link"
          >
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {rest.slice(0, 6).map((p) => (
            <ArticleCard key={p.slug} post={p} />
          ))}
        </div>
        <div className="mt-14 md:hidden text-center">
          <Link to="/blog" className="btn-secondary">See all →</Link>
        </div>
      </section>

      <GlossaryBand />
    </div>
  );
}
